/**
 * Chart data filtering utilities
 *
 * Functions to filter ParsedTable data by rows and columns
 */

import type { ParsedTable } from '../types'
import { GeneratorError } from '../generators/helpers'

/**
 * Filter a ParsedTable by selected rows and columns
 *
 * @param table - The original ParsedTable
 * @param selectedRows - Array of row names to include (empty = all)
 * @param selectedColumns - Array of column names to include (empty = all)
 * @param selectedRowPositions - Row positions to include (#1808). When non-empty
 *   they win over `selectedRows`: a name matches every row carrying it, so two
 *   rows labelled "Sky" could not be picked apart. Table order is kept.
 * @returns Filtered ParsedTable
 * @throws {GeneratorError} If filtering removes all data
 */
export function filterTableData(
  table: ParsedTable,
  selectedRows: string[] = [],
  selectedColumns: string[] = [],
  selectedRowPositions?: number[],
): ParsedTable {
  // Filter rows
  const filteredRows =
    selectedRowPositions && selectedRowPositions.length > 0
      ? table.rows.filter((row, index) => row.length > 0 && selectedRowPositions.includes(index))
      : selectedRows && selectedRows.length > 0
        ? table.rows.filter((row) => row.length > 0 && selectedRows.includes(row[0]))
        : table.rows

  console.log('filterTableData - Filtered rows count:', filteredRows.length)

  // Filter column headers and indices
  const originalColumnHeaders = table.headers.slice(1) // Exclude first header (row label)
  console.log('filterTableData - Original column headers:', originalColumnHeaders)

  const filteredColumnHeaders =
    selectedColumns && selectedColumns.length > 0
      ? originalColumnHeaders.filter((header) => selectedColumns.includes(header))
      : originalColumnHeaders

  console.log('filterTableData - Filtered column headers:', filteredColumnHeaders)

  // Determine indices of selected columns in original header list
  const selectedColumnIndices = selectedColumns && selectedColumns.length > 0
    ? originalColumnHeaders
        .map((header, index) => (selectedColumns.includes(header) ? index : -1))
        .filter((index) => index !== -1)
    : originalColumnHeaders.map((_, index) => index)

  // Validate we still have data
  if (filteredRows.length === 0) {
    throw new GeneratorError('Filtering removed all rows. No data to display.')
  }
  if (filteredColumnHeaders.length === 0) {
    throw new GeneratorError('Filtering removed all columns. No data to display.')
  }

  // Build filtered table data
  const filteredTableRows = filteredRows.map((row) => {
    const rowLabel = row[0] || ''
    const filteredCells = selectedColumnIndices.map((originalIndex) => {
      const dataIndex = originalIndex + 1 // Adjust for row label at index 0
      return dataIndex < row.length ? row[dataIndex] : '0'
    })
    return [rowLabel, ...filteredCells]
  })

  return {
    headers: [''].concat(filteredColumnHeaders),
    rows: filteredTableRows,
    rowCnt: filteredTableRows.length,
    colCnt: filteredColumnHeaders.length + 1,
  }
}

/**
 * Get indices of selected columns in the original header list
 *
 * @param originalHeaders - Full list of column headers (excluding first)
 * @param selectedColumns - Array of selected column names
 * @returns Array of indices in original list
 */
export function getSelectedColumnIndices(
  originalHeaders: string[],
  selectedColumns: string[]
): number[] {
  if (!selectedColumns || selectedColumns.length === 0) {
    return originalHeaders.map((_, index) => index)
  }

  return originalHeaders
    .map((header, index) => (selectedColumns.includes(header) ? index : -1))
    .filter((index) => index !== -1)
}
